import './AccountSelect.scss';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { MdCancel } from 'react-icons/md';
import { Placement, Size } from '@Components/designConstants';
import {
  accountKey,
  BillingAccount,
  isTVADAccount,
  isYouTubeAccount,
  TVADBillingAccount,
  YouTubeBillingAccount
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { Dropdown } from '@Components/Dropdown';
import { DropdownFilterPill, MenuItem } from '@Components/Dropdown/dropdown-constants';
import { InteractiveSpan } from '@Components/InteractiveEl';
import {
  accountLabel,
  AccountOption,
  getSection,
  SECTION_NON_TVAD,
  SECTION_TVAD,
  SECTION_YOUTUBE,
  ThreeLineOption,
  TVAD_FILTER_PILLS
} from './billing-utils';

const SECTION_MAPPED = 'Mapped to another Relationship';

interface AccountOptionLabelProps {
  label: string;
  value: string;
  tertiaryLabel?: string;
}

const AccountOptionLabel = ({
  label,
  value,
  tertiaryLabel
}: AccountOptionLabelProps): ReactNode => (
  <ThreeLineOption label={label} subLabel={`Account ID: ${value}`} tertiaryLabel={tertiaryLabel} />
);

interface AccountSelectProps {
  options: AccountOption[];
  value: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[];
  onChange: (accounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]) => void;
  disabledAccounts?: {
    account: BillingAccount | TVADBillingAccount | YouTubeBillingAccount;
    mappedToRelName: string;
  }[];
}

export const AccountSelect = ({
  options,
  value,
  onChange,
  disabledAccounts = []
}: AccountSelectProps) => {
  const [show, setShow] = useState(false);

  const tertiaryByValue = useMemo(
    () =>
      new Map(
        disabledAccounts.map(({ account: a, mappedToRelName }) => [
          accountKey(a),
          `Mapped to: ${mappedToRelName}`
        ])
      ),
    [disabledAccounts]
  );

  const renderOptionLabel = useCallback(
    (option: { label: string; value: string }): ReactNode => (
      <AccountOptionLabel
        label={option.label}
        value={option.value}
        tertiaryLabel={tertiaryByValue.get(option.value)}
      />
    ),
    [tertiaryByValue]
  );

  const buildMenuItems = useCallback(
    (selected: Set<string>): MenuItem<string>[] => {
      const accountItems = options.map(o => ({
        label: o.label,
        value: o.value,
        // Display-only grouping — from a backend calculation perspective YouTube still goes
        // through the non-TVAD pipeline (see getConfigAccountCategory in billing-utils.tsx).
        section: getSection(isYouTubeAccount(o.account), isTVADAccount(o.account)),
        selected: selected.has(o.value)
      }));

      const disabledItems = disabledAccounts.map(({ account: a }) => ({
        label: accountLabel(a),
        value: accountKey(a),
        section: SECTION_MAPPED,
        disabled: true,
        selected: false
      }));

      return [...accountItems, ...disabledItems];
    },
    [options, disabledAccounts]
  );

  const selectedKeys = useMemo(() => new Set(value.map(accountKey)), [value]);

  const [stagedKeys, setStagedKeys] = useState<Set<string>>(selectedKeys);

  const menuItems = useMemo(() => buildMenuItems(stagedKeys), [buildMenuItems, stagedKeys]);

  // Resolve a selection key back to its account. Includes the committed `value`
  // alongside `options` so a currently-selected account is never dropped just
  // because it isn't present in the available-options pool.
  const accountByKey = useMemo(() => {
    const map = new Map<string, BillingAccount | TVADBillingAccount | YouTubeBillingAccount>();
    for (const a of value) map.set(accountKey(a), a);
    for (const o of options) map.set(o.value, o.account);
    return map;
  }, [options, value]);

  const resetStaged = useCallback(() => {
    setStagedKeys(selectedKeys);
  }, [selectedKeys]);

  const toggleStaged = useCallback((key: string) => {
    setStagedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleToggle = useCallback(
    (opening: boolean) => {
      // Seed staged from the committed value on open; discard staged on close —
      // closing without Apply reverts to the committed selection (same as Cancel).
      resetStaged();
      setShow(opening);
    },
    [resetStaged]
  );

  const handleApply = useCallback(() => {
    const accounts = [...stagedKeys].flatMap(key => {
      const account = accountByKey.get(key);
      return account ? [account] : [];
    });
    onChange(accounts);
    setShow(false);
  }, [stagedKeys, accountByKey, onChange]);

  const handleCancel = useCallback(() => {
    resetStaged();
    setShow(false);
  }, [resetStaged]);

  const filterPills: DropdownFilterPill<string>[] = [...TVAD_FILTER_PILLS];

  if (disabledAccounts.length > 0) {
    filterPills.push({ label: 'Mapped Accounts', filter: i => i.section === SECTION_MAPPED });
  }

  const handleRemoveChip = useCallback(
    (key: string) => {
      if (show) {
        setStagedKeys(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      } else {
        onChange(value.filter(a => accountKey(a) !== key));
      }
    },
    [show, value, onChange]
  );

  // Chips reflect the staged buffer while open and the committed value while
  // closed, so external value changes (e.g. a parent "Clear") always re-render.
  const displayKeys = show ? stagedKeys : selectedKeys;
  const selectedChips = [...displayKeys].flatMap(key => {
    const account = accountByKey.get(key);
    return account ? [{ key, label: accountLabel(account) }] : [];
  });

  return (
    <div className='account-select-field'>
      <p className='account-select-label'>
        Account(s)<span className='billing-asterisk'>*</span>
      </p>
      <Dropdown<string>
        size={Size.LG}
        overlayPlacement={Placement.RIGHT}
        multiSelect
        hasTicks
        hasFilterBar
        collapsibleSections
        dropdownMenuClassName='account-select-menu'
        show={show}
        onToggle={opening => handleToggle(opening)}
        menuItems={menuItems}
        value=''
        onChange={() => {}}
        itemOnClick={item => {
          if (item.section !== SECTION_MAPPED) {
            toggleStaged(item.value);
          }
        }}
        section={[
          { title: SECTION_TVAD },
          { title: SECTION_YOUTUBE },
          { title: SECTION_NON_TVAD },
          { title: SECTION_MAPPED }
        ]}
        filterPills={filterPills}
        filterBarPlaceholder='Search'
        applyOnClick={handleApply}
        cancelOnClick={handleCancel}
        placeholder='Choose a platform / account'
        valueContent={
          selectedChips.length > 0 ? (
            <div className='account-select-chips'>
              {selectedChips.map(item => (
                <span key={item.key} className='account-select-chip'>
                  {item.label}
                  <InteractiveSpan
                    className='account-select-chip-remove'
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveChip(item.key);
                    }}
                  >
                    <MdCancel />
                  </InteractiveSpan>
                </span>
              ))}
            </div>
          ) : undefined
        }
        formatOptionLabel={renderOptionLabel}
      />
    </div>
  );
};
